import React from 'react'
import { observer } from 'mobx-react-lite'

import Avatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'

import { P } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import {
  Dialog,
  Drawer,
  useCategoryStore,
  useGlobalCopyStore,
  useEvent,
  useTransactionStore,
  ANALYTICS_EVENTS,
} from '../../../../../common'
import type { Transaction } from '../../../../../core'

import { useTransactionsUiStore } from '../../../../context/hooks'
import { ON_CLICK_EVENTS } from '../../../../constants/OnClickEvents'

import { SplitManager } from '../../../splitmanager'

import TransactionAmountHeader from '../TransactionAmountHeader'
import { useAriaLive } from '../../../../../common/hooks/useAriaLive'
import { buildDynamicCopy } from '../../../../../core/utils/Localization'

interface SplitActionProps {
  transaction: Transaction
}

const SplitAction: React.FC<SplitActionProps> = ({ transaction }) => {
  const { onEvent } = useEvent()
  const { announce, ariaLive } = useAriaLive()
  const { splitTransaction, unSplitTransaction, transactionSplitting } = useTransactionStore()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()
  const { setShouldDisableDrawerScroll } = useTransactionsUiStore()
  const { categories } = useCategoryStore()
  const theme = useTheme()
  const INITIAL_SPLIT = {
    ...transaction,
    amount: transaction.amount - 1,
    guid: '',
    has_been_split: false,
    parent_guid: transaction.guid,
  }

  const EMPTY_SPLIT = {
    ...transaction,
    amount: 1,
    category_guid: undefined,
    category: copy.split_transaction_select_category_btn,
    guid: '',
    has_been_split: false,
    parent_guid: transaction.guid,
    top_level_category_guid: undefined,
  }

  const [isSplitDrawerOpen, setIsSplitDrawerOpen] = React.useState(false)
  const [isDeleteSplitOpen, setIsDeleteSplitOpen] = React.useState(false)
  const [splits, setSplits] = React.useState<Transaction[]>([])

  const handleAddSplit = () => {
    const updatedSplits = [...splits]

    updatedSplits.push({ ...EMPTY_SPLIT })
    updatedSplits[0].amount -= 1
    setSplits(updatedSplits)

    announce(
      buildDynamicCopy(
        copy.split_transaction_add_split_announcement,
        updatedSplits.length,
      ) as string,
    )
  }

  const handleDeleteSplit = (index: number) => {
    if (index > 0) {
      const row = splits[index]
      const updatedSplits = [...splits]

      updatedSplits.splice(index, 1)
      updatedSplits[0].amount += row.amount
      setSplits(updatedSplits)

      announce(
        buildDynamicCopy(copy.split_transaction_delete_split_announcement, index + 1) as string,
      )
    }
  }

  const handleUpdateAmount = (index: number, amount: number) => {
    const updatedSplits = [...splits]

    updatedSplits[index].amount = amount
    updatedSplits[0].amount =
      transaction.amount -
      updatedSplits.reduce(
        (sum: number, split: Transaction, index) => sum + (index !== 0 ? split.amount : 0),
        0,
      )
    setSplits(updatedSplits)
  }

  const handleCategoryChanged = (index: number, categoryGuid: string) => {
    const updatedSplits = [...splits]
    const category = categories.find((item) => item.guid === categoryGuid)
    if (category) {
      updatedSplits[index] = {
        ...updatedSplits[index],
        category_guid: categoryGuid,
        category: category.name,
        top_level_category_guid: category.parent_guid || categoryGuid,
      }
      setSplits(updatedSplits)
      announce(
        buildDynamicCopy(
          copy.split_transaction_select_category_announcement,
          category.name,
          index + 1,
        ) as string,
      )
    }
  }

  const handleClick = () => {
    if (transaction.parent_guid || transaction.has_been_split) {
      setIsDeleteSplitOpen(true)
      return
    }

    setSplits([{ ...INITIAL_SPLIT }, { ...EMPTY_SPLIT }])

    onEvent(ANALYTICS_EVENTS.TRANSACTION_DETAILS_CLICK_SPLIT, {
      transaction_guid: transaction.guid,
    })
    setIsSplitDrawerOpen(true)
  }

  const handleSave = async () => {
    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_SPLIT_SAVE_CLICK)
    await splitTransaction({ ...transaction, has_been_split: true }, splits)
    setIsSplitDrawerOpen(false)
  }

  const handleCancel = async () => {
    if (transactionSplitting) return

    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_SPLIT_CANCEL_CLICK)
    setSplits([{ ...INITIAL_SPLIT }, { ...EMPTY_SPLIT }])
    setIsSplitDrawerOpen(false)
  }

  const handleUnSplit = async () => {
    onEvent(ANALYTICS_EVENTS.TRANSACTION_DETAILS_CLICK_UNSPLIT, {
      transaction_guid: transaction.guid,
    })
    const guid = transaction.parent_guid ?? transaction.guid
    await unSplitTransaction(guid)
  }

  const hasError = React.useMemo(() => {
    const hasNoSplits = splits.length <= 1
    const hasCategoryError = splits.some((split) => !split.category_guid)
    const hasAmountError = splits.some((split) => split.amount <= 0)

    return hasNoSplits || hasCategoryError || hasAmountError
  }, [splits])

  const bgColor = theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'

  return (
    <React.Fragment>
      {transaction && (
        <ListItem disableGutters={true} disablePadding={true}>
          <ListItemButton onClick={handleClick}>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: bgColor }} variant="rounded">
                <Icon name="call_split" sx={{ color: 'text.primary' }} />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                transaction.has_been_split || transaction.parent_guid
                  ? copy.split_transaction_unsplit_title
                  : copy.split_transaction_title
              }
              secondary={
                transaction.has_been_split || transaction.parent_guid
                  ? copy.split_transaction_unsplit_subtitle
                  : copy.split_transaction_desc
              }
            />
            <ListItemIcon>
              {transaction.has_been_split || transaction.parent_guid ? (
                <Icon name="delete" />
              ) : (
                <Icon name="chevron_right" sx={{ color: 'text.primary' }} />
              )}
            </ListItemIcon>
          </ListItemButton>

          <Drawer
            ariaLabelClose={commonCopy.close_aria}
            isOpen={isSplitDrawerOpen}
            isPrimaryDisabled={hasError || transactionSplitting}
            onClose={handleCancel}
            onPrimaryAction={handleSave}
            primaryText={commonCopy.save_button}
            secondaryText={commonCopy.cancel_button}
            title={copy.split_transaction_title}
          >
            {ariaLive}
            <TransactionAmountHeader transaction={transaction} />
            <SplitManager
              onAddSplit={handleAddSplit}
              onCategoryChanged={handleCategoryChanged}
              onDeleteSplit={handleDeleteSplit}
              onUpdateAmount={handleUpdateAmount}
              splits={splits}
              transaction={transaction}
            />
          </Drawer>

          <Dialog
            copy={{ title: copy.split_transaction_unsplit_title }}
            isOpen={isDeleteSplitOpen}
            onClose={() => {
              setShouldDisableDrawerScroll(false)
              setIsDeleteSplitOpen(false)
            }}
            onPrimaryAction={handleUnSplit}
            onSecondaryAction={() => {
              onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_UNSPLIT_CANCEL)
            }}
            primaryText={commonCopy.delete_button}
            secondaryText={commonCopy.cancel_button}
          >
            <P variant="subtitle1">
              {
                buildDynamicCopy(
                  copy.split_transaction_unsplit_description,
                  transaction.description,
                ) as string
              }
            </P>
          </Dialog>
        </ListItem>
      )}
    </React.Fragment>
  )
}

export default observer(SplitAction)
